// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import WysiwygEditor from '@/components/ui/wysiwyg-editor';
import { NewsletterSchedule } from '@/hooks/useNewsletterSchedules';
import { toast } from 'sonner';

interface NewsletterScheduleFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  editingSchedule?: NewsletterSchedule | null;
}

interface Template {
  id: string;
  name: string;
}

interface AudienceSegment {
  id: string;
  segment_name: string;
  estimated_recipients?: number;
}

interface Sport {
  id: string;
  name: string;
  gender: string;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function NewsletterScheduleFormDialog({
  open,
  onClose,
  onSubmit,
  editingSchedule,
}: NewsletterScheduleFormDialogProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [segments, setSegments] = useState<AudienceSegment[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    newsletter_name: '',
    template_id: '',
    audience_segment_id: '',
    day_of_week: 1,
    send_time: '09:00',
    is_active: true,
    include_rss_feeds: false,
    include_latest_athletes: false,
    include_latest_events: false,
    include_latest_camps: false,
    custom_content_html: '',
    gender: '',
    sport: '',
    rss_feeds: [] as Array<{ name: string; url: string; enabled: boolean }>
  });

  // Load templates and segments when dialog opens
  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  // Update form data when editingSchedule changes or dialog opens
  useEffect(() => {
    if (open) {
      if (editingSchedule) {
        console.log('📝 Loading schedule for editing:', JSON.stringify(editingSchedule, null, 2));
        const sendTime = editingSchedule.send_time?.substring(0, 5) || '09:00';
        const newFormData = {
          newsletter_name: editingSchedule.newsletter_name || '',
          template_id: editingSchedule.template_id || '',
          audience_segment_id: editingSchedule.audience_segment_id || '',
          day_of_week: editingSchedule.day_of_week ?? 1,
          send_time: sendTime,
          is_active: editingSchedule.is_active ?? true,
          include_rss_feeds: editingSchedule.include_rss_feeds ?? false,
          include_latest_athletes: editingSchedule.include_latest_athletes ?? false,
          include_latest_events: editingSchedule.include_latest_events ?? false,
          include_latest_camps: editingSchedule.include_latest_camps ?? false,
          custom_content_html: editingSchedule.custom_content_html || '',
          gender: editingSchedule.gender || '',
          sport: editingSchedule.sport || '',
          rss_feeds: (editingSchedule as any).rss_feeds || []
        };
        setFormData(newFormData);
        console.log('📝 Form data after setting:', JSON.stringify(newFormData, null, 2));
        console.log('📝 Available templates:', JSON.stringify(templates, null, 2));
        console.log('📝 Available segments:', JSON.stringify(segments, null, 2));
      } else {
        console.log('📝 Resetting form for new schedule');
        setFormData({
          newsletter_name: '',
          template_id: '',
          audience_segment_id: '',
          day_of_week: 1,
          send_time: '09:00',
          is_active: true,
          include_rss_feeds: false,
          include_latest_athletes: false,
          include_latest_events: false,
          include_latest_camps: false,
          custom_content_html: '',
          gender: '',
          sport: '',
          rss_feeds: []
        });
      }
    }
  }, [open, editingSchedule?.id]);

  const fetchData = async () => {
    setIsLoading(true);
    const [templatesData, segmentsData, sportsData] = await Promise.all([
      supabase.from('communication_templates').select('id, name').order('name'),
      supabase.from('email_audience_segments').select('id, segment_name, estimated_recipients').eq('is_active', true).order('segment_name'),
      supabase.from('sports').select('id, name, gender').order('name')
    ]);
    
    setTemplates(templatesData.data || []);
    setSegments(segmentsData.data || []);
    setSports(sportsData.data || []);
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields with user feedback
    if (!formData.newsletter_name?.trim()) {
      toast.error('Newsletter name is required');
      return;
    }
    
    if (!formData.template_id) {
      toast.error('Email template is required');
      return;
    }
    
    if (!formData.audience_segment_id) {
      toast.error('Target audience is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="newsletter_name">Newsletter Name *</Label>
        <Input
          id="newsletter_name"
          value={formData.newsletter_name}
          onChange={(e) => setFormData({ ...formData, newsletter_name: e.target.value })}
          placeholder="e.g., Weekly Basketball Update"
          required
        />
      </div>

      <div className="space-y-2">
            <Label htmlFor="template_id">Email Template *</Label>
            <Select
              value={formData.template_id}
              onValueChange={(value) => setFormData({ ...formData, template_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">
                    No templates available
                  </div>
                ) : (
                  templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {formData.template_id && <p className="text-xs text-muted-foreground">Selected: {templates.find(t => t.id === formData.template_id)?.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="audience_segment_id">Target Audience *</Label>
            <Select
              value={formData.audience_segment_id}
              onValueChange={(value) => setFormData({ ...formData, audience_segment_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select audience segment" />
              </SelectTrigger>
              <SelectContent>
                {segments.map((segment) => (
                  <SelectItem key={segment.id} value={segment.id}>
                    {segment.segment_name}
                    {segment.estimated_recipients !== undefined && 
                      ` (${segment.estimated_recipients} recipients)`
                    }
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.audience_segment_id && <p className="text-xs text-muted-foreground">Selected: {segments.find(s => s.id === formData.audience_segment_id)?.segment_name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => {
                  setFormData({ ...formData, gender: value, sport: '' });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
              {formData.gender && <p className="text-xs text-muted-foreground">Selected: {formData.gender}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sport">Sport</Label>
              <Select
                value={formData.sport}
                onValueChange={(value) => setFormData({ ...formData, sport: value })}
                disabled={!formData.gender}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formData.gender ? "Select sport" : "Select gender first"} />
                </SelectTrigger>
                <SelectContent>
                  {sports
                    .filter(sport => {
                      // Map form gender to sport gender values
                      const genderMap: Record<string, string[]> = {
                        'male': ['Men', 'Male'],
                        'female': ['Women', 'Female']
                      };
                      const genderValues = genderMap[formData.gender] || [];
                      return genderValues.includes(sport.gender);
                    })
                    .map(sport => (
                      <SelectItem key={sport.id} value={sport.name}>
                        {sport.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
              {formData.sport && <p className="text-xs text-muted-foreground">Selected: {formData.sport}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="day_of_week">Day of Week *</Label>
              <Select
                value={formData.day_of_week.toString()}
                onValueChange={(value) => setFormData({ ...formData, day_of_week: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map((day) => (
                    <SelectItem key={day.value} value={day.value.toString()}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="send_time">Send Time *</Label>
              <Input
                id="send_time"
                type="time"
                value={formData.send_time}
                onChange={(e) => setFormData({ ...formData, send_time: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active (schedule will send automatically)</Label>
          </div>

          <div className="space-y-3">
            <Label>Content Includes</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_rss_feeds"
                  checked={formData.include_rss_feeds}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, include_rss_feeds: checked as boolean })
                  }
                />
                <Label htmlFor="include_rss_feeds" className="font-normal">
                  Include RSS Feeds (from custom feeds below)
                </Label>
              </div>
              
              {/* RSS Feeds Management */}
              {formData.include_rss_feeds && (
                <div className="ml-6 space-y-3 p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Custom RSS Feeds (max 3)</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (formData.rss_feeds.length < 3) {
                          setFormData({
                            ...formData,
                            rss_feeds: [...formData.rss_feeds, { name: '', url: '', enabled: true }]
                          });
                        } else {
                          toast.info('Maximum 3 RSS feeds allowed');
                        }
                      }}
                      disabled={formData.rss_feeds.length >= 3}
                    >
                      + Add Feed
                    </Button>
                  </div>
                  
                  {formData.rss_feeds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No RSS feeds added yet. Click "Add Feed" to add one.</p>
                  ) : (
                    <div className="space-y-3">
                      {formData.rss_feeds.map((feed, index) => (
                        <div key={index} className="space-y-2 p-3 border rounded bg-background">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium">Feed {index + 1}</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newFeeds = formData.rss_feeds.filter((_, i) => i !== index);
                                setFormData({ ...formData, rss_feeds: newFeeds });
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                          <Input
                            placeholder="Feed Name (e.g., NCAA News)"
                            value={feed.name}
                            onChange={(e) => {
                              const newFeeds = [...formData.rss_feeds];
                              newFeeds[index].name = e.target.value;
                              setFormData({ ...formData, rss_feeds: newFeeds });
                            }}
                          />
                          <Input
                            placeholder="RSS Feed URL"
                            value={feed.url}
                            onChange={(e) => {
                              const newFeeds = [...formData.rss_feeds];
                              newFeeds[index].url = e.target.value;
                              setFormData({ ...formData, rss_feeds: newFeeds });
                            }}
                          />
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`feed-enabled-${index}`}
                              checked={feed.enabled}
                              onCheckedChange={(checked) => {
                                const newFeeds = [...formData.rss_feeds];
                                newFeeds[index].enabled = checked as boolean;
                                setFormData({ ...formData, rss_feeds: newFeeds });
                              }}
                            />
                            <Label htmlFor={`feed-enabled-${index}`} className="text-xs font-normal">
                              Enabled
                            </Label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_latest_athletes"
                  checked={formData.include_latest_athletes}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, include_latest_athletes: checked as boolean })
                  }
                />
                <Label htmlFor="include_latest_athletes" className="font-normal">
                  Include Latest Athletes
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_latest_events"
                  checked={formData.include_latest_events}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, include_latest_events: checked as boolean })
                  }
                />
                <Label htmlFor="include_latest_events" className="font-normal">
                  Include Latest Events
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_latest_camps"
                  checked={formData.include_latest_camps}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, include_latest_camps: checked as boolean })
                  }
                />
                <Label htmlFor="include_latest_camps" className="font-normal">
                  Include Latest Camps
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom_content_html">Custom Content (HTML)</Label>
            <WysiwygEditor
              value={formData.custom_content_html}
              onChange={(value) => setFormData({ ...formData, custom_content_html: value })}
              placeholder="Add custom HTML content for this newsletter..."
            />
          </div>

          <div className="rounded-lg bg-muted p-4 space-y-2">
            <h4 className="font-medium">Header, Footer & Ads</h4>
            <p className="text-sm text-muted-foreground">
              Header, footer, and ad slots are automatically inherited from the selected email template. 
              To customize these elements, edit the template in the Communication Templates section.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : editingSchedule ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
  );
}